import React, { createContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  clearLegacyStoredUser,
  clearSessionAuthToken,
  setSessionAuthToken,
} from "../helpers/global";
import { authService } from "../services/authService";

export const UserContext = createContext();

const applyAuthSession = (userResponse) => {
  if (userResponse?.token) {
    setSessionAuthToken(userResponse.token);
  }
};

const clearAuthSession = () => {
  clearSessionAuthToken();
};

const GET_ME_RATE_LIMIT_RETRIES = 4;
const GET_ME_RATE_LIMIT_BASE_DELAY_MS = 1000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchMeWithRateLimitRetry = async () => {
  let lastResult = {};

  for (let attempt = 0; attempt <= GET_ME_RATE_LIMIT_RETRIES; attempt++) {
    lastResult = await authService.getMe();
    if (!lastResult.rateLimited) return lastResult;
    if (attempt < GET_ME_RATE_LIMIT_RETRIES) {
      await wait(GET_ME_RATE_LIMIT_BASE_DELAY_MS * (attempt + 1));
    }
  }

  return lastResult;
};

export const UserProvider = ({ children }) => {
  const [gettingMe, setGettingMe] = useState(true);
  const [user, setUser] = useState(null);
  const [loggingIn, setLoggingIn] = useState(false);
  const [registrationOtpGenerated, setRegistrationOtpGenerated] =
    useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [passwordResetSuccessfull, setPasswordResetSuccessfull] =
    useState(false);
  const [forgotPasswordInProgress, setForgotPasswordInProgress] =
    useState(false);
  const [forgotPasswordSuccessfull, setForgotPasswordSuccessfull] =
    useState(false);

  const isLoggedIn = Boolean(user);

  const hydrateUser = async (fallback) => {
    const { response: me, unauthorized, rateLimited } =
      await fetchMeWithRateLimitRetry();

    if (me) {
      applyAuthSession(me);
      setUser(me);
      return;
    }

    if (unauthorized) {
      clearAuthSession();
      setUser(null);
      return;
    }

    // Rate limit / transient errors must not clear a just-established session.
    if (fallback) {
      applyAuthSession(fallback);
      setUser(fallback);
      return;
    }

    if (rateLimited) {
      toast.error("Too many requests. Please wait a moment and try again.");
      return;
    }

    setUser(null);
  };

  const getMe = async ({ silent = false } = {}) => {
    setGettingMe(true);
    const { response, unauthorized, rateLimited } =
      await fetchMeWithRateLimitRetry();

    if (response) {
      applyAuthSession(response);
      setUser(response);
      setGettingMe(false);
      return;
    }

    if (unauthorized) {
      clearAuthSession();
      setUser(null);
      setGettingMe(false);
      return;
    }

    if (rateLimited) {
      // Keep gettingMe true so ProtectedRoute shows a loader instead of /login.
      if (!silent) {
        toast.error("Too many requests. Please wait a moment and try again.");
      }
      setTimeout(() => getMe({ silent: true }), 5000);
      return;
    }

    setGettingMe(false);
  };

  const signIn = async (email, password) => {
    setLoggingIn(true);

    const { response, error } = await authService.signIn(email, password);
    if (error) {
      toast.error(error);
      setLoggingIn(false);
      return;
    }

    await hydrateUser(response);
    setLoggingIn(false);
  };

  const signOut = async () => {
    await authService.signOut();
    clearAuthSession();
    setUser(null);
  };

  const register = async (formData) => {
    setLoggingIn(true);

    const { response, error } = await authService.register(formData);
    if (error) {
      toast.error(error);
      setLoggingIn(false);
      return;
    }

    await hydrateUser(response);
    setLoggingIn(false);
  };

  const generateRegistrationOTP = async (email) => {
    setLoggingIn(true);

    const { response, error } =
      await authService.generateRegistrationOTP(email);
    if (error) {
      toast.error(error);
      setLoggingIn(false);
      return;
    }

    if (response) {
      setRegistrationOtpGenerated(true);
      toast.success("OTP is sent successfully to your email.");
    }
    setLoggingIn(false);
  };

  const resetPassword = async (formData) => {
    setResettingPassword(true);

    const { error } = await authService.resetPassword({
      email: formData?.email,
      temporaryKey: formData?.temporaryKey,
      newPassword: formData?.newPassword,
    });

    if (error) {
      setResettingPassword(false);
      return toast.error(error);
    }

    setPasswordResetSuccessfull(true);
    setForgotPasswordSuccessfull(false);
    setResettingPassword(false);
    toast.success("Password Reset Successfull.");
  };

  const forgotPassword = async (email) => {
    setForgotPasswordInProgress(true);

    const { error } = await authService.forgotPassword(email);
    if (error) {
      setForgotPasswordInProgress(false);
      return toast.error(error);
    }

    toast.success("An OTP has been sent to your email address");
    setForgotPasswordSuccessfull(true);
    setForgotPasswordInProgress(false);
  };

  useEffect(() => {
    clearLegacyStoredUser();
    getMe();
  }, []);

  return (
    <UserContext.Provider
      value={{
        user,
        isLoggedIn,
        gettingMe,
        loggingIn,
        registrationOtpGenerated,
        passwordResetSuccessfull,
        resettingPassword,
        forgotPasswordInProgress,
        forgotPasswordSuccessfull,
        onUserChange: setUser,
        updateRegistrationOTPvalue: setRegistrationOtpGenerated,
        updateForgotPasswordOTPvalue: setForgotPasswordSuccessfull,
        signIn,
        signOut,
        register,
        generateRegistrationOTP,
        resetPassword,
        forgotPassword,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
